import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import { transactionsTypes } from 'app-components/flux/transactions';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';

import QAmountField from 'components/QAmountField';

import InvestmentTransactionBaseForm from './InvestmentTransactionBaseForm';

const InvestmentTransactionReinvestForm = (props) => {
  const { formik } = props;

  useEffect(() => {
    formik.registerField('units', {
      validate: (value) => (typeof value === 'number' && value ? undefined : 'field is required'),
    });
    formik.registerField('costBasis', {
      validate: (value) => (typeof Number(value) === 'number' && Number(value) !== 0 ? undefined : 'use valid amount'),
    });
    return () => {
      formik.unregisterField('units');
      formik.unregisterField('costBasis');
    };
  }, [formik]);

  const distributionAmount = useMemo(() => {
    const commission = typeof formik.values.commission === 'number' ? Math.abs(formik.values.commission) : 0;
    const result = Math.abs(formik.values.costBasis) - commission;
    return Number.isNaN(result) ? undefined : result;
  }, [formik.values.costBasis, formik.values.commission]);

  const pricePerUnit = useMemo(() => {
    const result = Math.abs(distributionAmount / formik.values.units);
    return Number.isNaN(result) ? undefined : result;
  }, [distributionAmount, formik.values.units]);

  const reinvestLabelAmount = useMemo(() => {
    switch (formik.values.investmentTxnType) {
      case transactionsTypes.InvestmentTxnType.REINVEST_DIVIDEND:
        return 'Dividend';
      case transactionsTypes.InvestmentTxnType.REINVEST_INTEREST:
        return 'Interest';
      case transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
      case transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
        return 'Distribution Amount';
      default:
        verify(false, `reinvestLabelAmount: nexpected investmentTxnType = ${formik.values.investmentTxnType}`);
        return 'Dividend';
    }
  }, [formik.values.investmentTxnType]);

  return (
    <>
      <InvestmentTransactionBaseForm formik={formik} />

      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='investment-amount'
          editable
          disabled
          label='Amount'
          sx={{ minWidth: 110 }}
          noNegatives
          value={Math.abs(distributionAmount)}
        />
        /
        <TextField
          id='units'
          label='Number of shares'
          type='number'
          inputProps={{ min: 1 }}
          sx={{ minWidth: 110 }}
          value={formik.values.units != null ? Math.abs(formik.values.units) : ''}
          onChange={(event) => formik.setFieldValue('units', event.target.value ? Number(event.target.value) : null)}
          error={Boolean(formik.submitCount > 0 && formik.errors.units)}
          helperText={(formik.submitCount > 0 && formik.errors.units) || ''}
        />
        =
        <QAmountField
          id='price-per-share'
          editable
          disabled
          label='Price per share'
          sx={{ minWidth: 110 }}
          expectedSign='positive'
          value={pricePerUnit}
        />
      </Box>

      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='commission'
          editable
          label='Commission'
          sx={{ minWidth: 110 }}
          value={formik.values.commission}
          onChange={(event) => formik.setFieldValue('commission', Number(event.target.value))}
          error={Boolean(formik.submitCount > 0 && formik.errors.commission)}
          helperText={(formik.submitCount > 0 && formik.errors.commission) || ''}
        />
      </Box>

      <Divider sx={{ maxWidth: 320 }} />

      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='distribution-amount'
          editable
          label={reinvestLabelAmount}
          sx={{ minWidth: 110 }}
          noNegatives
          value={Math.abs(formik.values.costBasis)}
          onChange={(event) => formik.setFieldValue('costBasis', Number(event.target.value))}
          error={Boolean(formik.submitCount > 0 && formik.errors.costBasis)}
          helperText={(formik.submitCount > 0 && formik.errors.costBasis) || ''}
        />
      </Box>
    </>
  );
};

InvestmentTransactionReinvestForm.propTypes = {
  formik: PropTypes.object.isRequired,
};

export default InvestmentTransactionReinvestForm;
